import { PagePending } from "@/components/ui/PagePending";
import { AdminGroup, adminUsersQuery, useUpdateRoleMutation } from "@/api/user";
import { Badge } from "@/components/ui/Badge";
import { DataTable } from "@/components/data-table/DataTable";
import { DataTableBody } from "@/components/data-table/DataTableBody";
import { PageWrapper } from "@/components/ui/PageWrapper";
import { Checkbox, Field, Flex, Label, Select } from "@givenwell/components";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";

export const Route = createFileRoute("/admin/staff")({
  pendingComponent: PagePending,
  component: RouteComponent,
});

function RouteComponent() {
  const { data } = useSuspenseQuery(adminUsersQuery);
  const [showDeactivated, setShowDeactivated] = useState(false);

  const deactivatedCount = useMemo(() => data.users.filter(u => !u.active).length, [data.users]);
  const users = useMemo(() => {
    const visible = showDeactivated ? data.users : data.users.filter(u => u.active);
    // Active first, then by email. Sort a copy - the query cache hands us its own array.
    return [...visible].sort((a, b) => {
      if (a.active !== b.active) {
        return a.active ? -1 : 1;
      }
      // Email is nullable in practice (seeded accounts); keep those last rather than letting
      // an empty string sort them to the top.
      if (!a.email_address || !b.email_address) {
        return (a.email_address ? 0 : 1) - (b.email_address ? 0 : 1);
      }
      return a.email_address.localeCompare(b.email_address);
    });
  }, [data.users, showDeactivated]);

  return (
    <PageWrapper title="Givenwell Staff">
      <Field css={{ mb: 16 }}>
        <Flex css={{ flexDir: "row", gap: 8, alignItems: "center" }}>
          <Checkbox
            checked={showDeactivated}
            disabled={deactivatedCount === 0}
            onCheckedChange={c => setShowDeactivated(c === true)}
            css={{ fontSize: 14, fontWeight: 400 }}
          />
          <Label>{deactivatedCount > 0 ? `Show deactivated (${deactivatedCount})` : "Show deactivated"}</Label>
        </Flex>
      </Field>
      <DataTable>
        <DataTableBody
          dataKey={"user_id"}
          data={users}
          columns={[
            {
              header: "Name",
              cell: d => (
                <Flex css={{ flexDir: "row", gap: 6, alignItems: "center" }}>
                  {`${d.given_name} ${d.family_name}`}
                  {!d.active && <Badge color="gray">Deactivated</Badge>}
                </Flex>
              ),
            },
            {
              header: "Email",
              cell: "email_address",
            },
            {
              header: "Role",
              cell: d => <RoleSelect userId={d.user_id} currentRole={d.groups[0] || ""} />,
            },
            {
              header: "MFA",
              cell: d => <MfaBadge enrolled={d.mfa_enrolled} methods={d.mfa_methods} />,
            },
            {
              header: "Special Permissions",
              cell: d => (d.permissions.length > 0 ? d.permissions.join(", ") : "None"),
            },
          ]}
        />
      </DataTable>
    </PageWrapper>
  );
}

function MfaBadge({ enrolled, methods }: { enrolled: boolean; methods: string[] }) {
  if (!enrolled) {
    return <Badge color="red">None</Badge>;
  }
  // Name the factor where FusionAuth gives us one, so "enrolled" is checkable rather than trusted.
  return <Badge color="green">{methods.length > 0 ? methods.join(", ") : "Enrolled"}</Badge>;
}

const ASSIGNABLE_ROLES = [
  { value: AdminGroup.Default, label: "Default" },
  { value: AdminGroup.Support, label: "Support" },
];

function RoleSelect({ userId, currentRole }: { userId: string; currentRole: string }) {
  const mut = useUpdateRoleMutation();
  // A user may still be on a group the API no longer assigns (group.admin_dev) or on none at all.
  // Show that as-is rather than letting the select fall back to the first option.
  const isAssignable = ASSIGNABLE_ROLES.some(role => role.value === currentRole);
  return (
    <Select
      size="sm"
      css={{ width: 100 }}
      defaultValue={currentRole}
      onChange={e => {
        const newRole = e.target.value;
        mut.mutate({
          user_id: userId,
          group: newRole,
        });
      }}
    >
      {!isAssignable && (
        <option value={currentRole} disabled>
          {currentRole || "None"}
        </option>
      )}
      {ASSIGNABLE_ROLES.map(role => (
        <option key={role.value} value={role.value}>
          {role.label}
        </option>
      ))}
    </Select>
  );
}
